import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const nswCouncils = [
  'Bayside Council', 'Blacktown City Council', 'Blue Mountains City Council', 'Burwood Council',
  'Camden Council', 'Campbelltown City Council', 'Canterbury-Bankstown Council', 'Central Coast Council',
  'City of Canada Bay Council', 'City of Parramatta Council', 'City of Ryde Council', 'City of Sydney Council',
  'Cumberland Council', 'Fairfield City Council', 'Georges River Council', 'Hawkesbury City Council',
  'Hornsby Shire Council', "Hunter's Hill Council", 'Inner West Council', 'Ku-ring-gai Council',
  'Lane Cove Council', 'Liverpool City Council', 'Mosman Municipal Council', 'North Sydney Council',
  'Northern Beaches Council', 'Penrith City Council', 'Randwick City Council', 'Strathfield Council',
  'Sutherland Shire Council', 'The Hills Shire Council', 'Waverley Council', 'Willoughby City Council',
  'Woollahra Municipal Council'
];

const WmpNsw = () => (
  <div className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
    <SEO
      title="Waste Management Plans for NSW Developments | PlanGen Consulting"
      description="Generate council-compliant Waste Management Plans for NSW property developments. Covering 33 councils including Sydney, Parramatta, Canterbury-Bankstown, Northern Beaches. Operational WMP, D&CWMP, and CMP."
      keywords="waste management plan NSW, WMP NSW, waste management plan Sydney, council waste plan NSW, DA waste management plan, operational waste management plan NSW, DCWMP NSW, construction waste plan"
      canonicalPath="/waste-management-plan-nsw"
    />
    <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />

    {/* Nav */}
    <nav className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-white/70 border-b border-white/40 shadow-sm">
      <div className="max-w-4xl mx-auto px-6 flex items-center justify-between h-14">
        <Link to="/" className="text-lg font-bold tracking-tight text-[#0A2540]">PlanGen<span className="text-blue-600">Consulting</span></Link>
        <Link to="/register" className="px-5 py-1.5 rounded-full bg-[#0A2540] text-white text-sm font-medium hover:bg-black transition-colors">Get started free</Link>
      </div>
    </nav>

    {/* Content */}
    <article className="max-w-4xl mx-auto px-6 pt-24 pb-16">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-slate-400 mb-8">
        <Link to="/" className="hover:text-blue-600">Home</Link>
        <span>/</span>
        <span className="text-slate-600">Waste Management Plans NSW</span>
      </div>

      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold mb-4 border border-blue-200">
        <span>33 NSW Councils Covered</span>
      </div>

      <h1 className="text-4xl lg:text-5xl font-black tracking-tighter text-[#0A2540] mb-6 leading-[1.1]">
        Waste Management Plans<br />for NSW Developments
      </h1>

      <p className="text-lg text-slate-600 mb-10 leading-relaxed max-w-2xl">
        A Waste Management Plan (WMP) is a mandatory document for most Development Applications in New South Wales. It demonstrates how waste will be generated, stored, collected, and recycled throughout the life of your development.
      </p>

      {/* When do you need one */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">When Do You Need a WMP in NSW?</h2>
        <p className="text-base text-slate-600 mb-4 leading-relaxed">
          Most NSW councils require a Waste Management Plan as part of the following application types:
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          {[
            { abbr: 'DA', name: 'Development Application', desc: 'Standard planning approval for new builds, alterations, and change of use' },
            { abbr: 'CDC', name: 'Complying Development Certificate', desc: 'Fast-track approval for developments that meet predetermined standards' },
            { abbr: 'CC', name: 'Construction Certificate', desc: 'Required before construction begins on an approved DA' },
            { abbr: 'SSDA', name: 'State Significant Development', desc: 'Major projects assessed by the NSW Department of Planning' },
          ].map(t => (
            <div key={t.abbr} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="text-lg font-black text-[#0A2540] mb-1">{t.abbr}</div>
              <div className="text-sm font-medium text-slate-700 mb-1">{t.name}</div>
              <div className="text-xs text-slate-500">{t.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Types of WMP */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">Types of Waste Management Plans for NSW</h2>
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-[#0A2540] mb-2">Operational Waste Management Plan (OpWMP)</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Covers ongoing waste management during the building's operational life. Includes waste generation rate calculations based on dwelling count and development type, bin sizing and configuration, collection frequency, storage room design, and recycling strategies. Most NSW councils mandate this for residential developments of 5+ dwellings and all commercial developments.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-[#0A2540] mb-2">Demolition &amp; Construction Waste Management Plan (D&amp;CWMP)</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Required for the demolition and construction phases. Details waste minimisation strategies, recycling targets (typically 80%+ diversion from landfill), disposal methods, and compliance with the NSW EPA waste classification guidelines. Councils expect specific material breakdowns — concrete, timber, metals, plasterboard — with nominated recycling facilities.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-[#0A2540] mb-2">Construction Management Plan (CMP)</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Addresses waste management during active construction, including site logistics, waste storage locations, collection vehicle access, dust and noise mitigation, and neighbour notification requirements. Often required alongside the D&amp;CWMP for larger developments.
            </p>
          </div>
        </div>
      </section>

      {/* What goes into a compliant WMP */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">What Goes Into a Compliant NSW WMP?</h2>
        <p className="text-base text-slate-600 mb-4 leading-relaxed">
          Each council has specific requirements set out in their Development Control Plan (DCP), but most NSW WMPs must include:
        </p>
        <ul className="space-y-2 text-sm text-slate-600">
          {[
            'Waste generation rates calculated per dwelling/unit type (residential) or per m² GFA (commercial)',
            'Bin type, size, and quantity for general waste, recycling, organics/FOGO, and bulky waste',
            'Collection frequency and nominated waste contractor arrangements',
            'Waste storage room dimensions, ventilation, drainage, and access for collection vehicles',
            'Swept path analysis confirming waste collection vehicle access (SRV, MRV, or HRV)',
            'Recycling and waste minimisation strategies aligned with the NSW Waste Avoidance and Resource Recovery Strategy',
            'Site plan showing bin storage locations, collection points, and vehicle manoeuvring areas',
            'Demolition waste estimates with nominated recycling facilities and diversion targets',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              {item}
            </li>
          ))}
        </ul>
      </section>

      {/* Common mistakes */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">Common Mistakes That Get DAs Rejected</h2>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <ul className="space-y-3 text-sm text-slate-700">
            {[
              'Using generic waste generation rates instead of council-specific DCP rates',
              'Undersized waste storage room — not enough space for the required number of bins',
              'No vehicle access analysis — councils increasingly require proof that collection trucks can enter and exit',
              'Missing FOGO (Food Organics Garden Organics) provision where council mandates it',
              'No consideration of bulky waste storage for residential developments',
              'Failing to account for commercial waste streams in mixed-use developments',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Council table */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">33 NSW Councils Covered</h2>
        <p className="text-base text-slate-600 mb-4 leading-relaxed">
          Our WMP generator includes council-specific waste generation rates, bin requirements, and DCP compliance parameters for all of the following NSW councils:
        </p>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
            {nswCouncils.map(name => (
              <div key={name} className="text-sm text-slate-600 py-1 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A2540] rounded-2xl p-10 text-center mb-12">
        <h2 className="text-2xl font-bold text-white mb-3">Generate Your NSW WMP Now</h2>
        <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">Free to sign up. Our generator calculates waste rates, sizes bins, and produces a council-compliant PDF — typically in under 30 minutes.</p>
        <div className="flex items-center justify-center gap-4">
          <Link to="/register" className="px-8 py-3 rounded-full bg-white text-[#0A2540] font-semibold text-sm hover:bg-slate-100 transition-colors">Get Started Free</Link>
          <Link to="/bespoke" className="px-8 py-3 rounded-full border border-white/20 text-white font-medium text-sm hover:bg-white/10 transition-colors">Need a Consultant Plan?</Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { q: 'How long does it take to generate a WMP for NSW?', a: 'Most users complete their Waste Management Plan in under 30 minutes using our online generator. Enter your project details (council, dwelling count, development type) and the system automatically calculates waste generation rates, bin configurations, and collection schedules.' },
            { q: 'Do I need separate plans for operational and construction waste?', a: 'Yes. Most NSW councils require both an Operational WMP (for ongoing building operations) and a Demolition & Construction WMP (for the build phase). Some councils also require a Construction Management Plan. Our platform generates all three.' },
            { q: 'What if my council isn\'t listed?', a: 'We currently cover 33 NSW councils. If your council isn\'t listed, contact us for a consultant plan prepared by our waste management experts. We can research your specific council\'s DCP requirements and prepare a tailored WMP.' },
            { q: 'Is the generated WMP accepted by councils?', a: 'Yes. Our WMPs are prepared using council-specific waste generation rates and DCP requirements. They are formatted as professional PDF documents suitable for inclusion in your DA submission pack.' },
          ].map((faq, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-base font-semibold text-[#0A2540] mb-2">{faq.q}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "How long does it take to generate a WMP for NSW?", "acceptedAnswer": { "@type": "Answer", "text": "Most users complete their Waste Management Plan in under 30 minutes using our online generator." }},
          { "@type": "Question", "name": "Do I need separate plans for operational and construction waste?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Most NSW councils require both an Operational WMP and a Demolition & Construction WMP." }},
          { "@type": "Question", "name": "Is the generated WMP accepted by councils?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Our WMPs are prepared using council-specific waste generation rates and DCP requirements." }},
        ]
      })}} />
    </article>

    {/* Footer */}
    <footer className="bg-[#0A2540] py-8">
      <div className="max-w-4xl mx-auto px-6 text-center text-xs text-slate-500">
        <p>&copy; {new Date().getFullYear()} PlanGen Consulting. Professional Indemnity &amp; Public Liability Insured.</p>
        <div className="mt-2">
          <Link to="/" className="text-slate-400 hover:text-white mr-4">Home</Link>
          <Link to="/waste-management-plan-vic" className="text-slate-400 hover:text-white mr-4">WMP Victoria</Link>
          <Link to="/what-is-a-waste-management-plan" className="text-slate-400 hover:text-white">What is a WMP?</Link>
        </div>
      </div>
    </footer>
  </div>
);

export default WmpNsw;
